import { defineStore } from 'pinia'
import { getDecryptedDataFromLocalStorage } from '@/helpers/jwtVerifyLocalStorage'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {
      _id: '', // Importante: nome no Vue 2 era *id_user*
      name: '',
      login: '', // Importante: nome no Vue 2 era *username*
      multi_user: false,
      mf: false,
      admin: false,
      profiles: [],
      permissions: [],
      ecommercePermissions: [],
      club: false,
      has_ecommerce: false,
      has_club: false,
      has_pii_access: false,
      has_pii_employees_access: false,
      has_pii_download_data_permission: false,
      account: {
        _id: '', // Importante: nome no Vue 2 era *id_account*
        name: '', // Importante: nome no Vue 2 era *client*
        id_external: 0,
        is_franchise: false,
        is_franchisee: false,
        has_mercapromo: false,
        only_mercapromo: false,
        tribanco: false,
        ecommerce_url: '',
        recencyCriteria: null,
        integrationType: '',
        db_name: '',
        security: null,
        modules: null,
        franchiseePermissions: {
          // Importante: no Vue 2 essa estrutura não ficava aqui, era um objeto na raiz do store global *franchiseePermissions*
          offers_mobile: false,
          banners_mobile: false,
          performance_mobile: false,
          push_campaigns: false,
          flyers_campaigns: false,
          edit_cashback: false,
          edit_points: false,
          edit_user_register: false,
          edit_offers_mobile_units: false,
          managing_user_requests: false,
          view_offers_schedule_trade_results: false,
        },
      },
    },
    useCatalogFlag: false,
    pagesControlledByFranchiseMap: {
      // Importante: No Vue 2 essa estrutura era chamada de franchiseePermissionsRoutes, e as chaves eram as permissões
      'mobile/ofertas/destaques': 'offers_mobile',
      'mobile/banners': 'banners_mobile',
      'mobile/desempenho': 'performance_mobile',
      'campanhas/aplicativo': 'push_campaigns',
      'mobile/encartes': 'flyers_campaigns',
      'mobile/cashback/ajustes': 'edit_cashback',
      'clientes/gerenciamento/cadastros/edicao-pontos': 'edit_points',
      'clientes/gerenciamento/cadastros/edicao-cadastros': 'edit_user_register',
      'clientes/gerenciamento/solicitacoes': 'managing_user_requests',
      'mobile/ofertas/produtos/edicao-lojas-franquia': 'edit_offers_mobile_units',
      'industria/ofertas_trade/resultados_franquia': 'view_offers_schedule_trade_results',
    },
  }),
  actions: {
    async parseDataFromVuex(value) {
      this.user.permissions = value.userPermissions
      this.user.account.franchiseePermissions = value.franchiseePermissions
    },
    async getDataFromLocalStorage() {
      const user = JSON.parse(window.localStorage.getItem('loggedUser'))
      if (!user) return

      try {
        const { isMultiUser } = await getDecryptedDataFromLocalStorage('is-multi-user', { isMultiUser: false })
        const { isClub } = await getDecryptedDataFromLocalStorage('is-club', { isClub: false })
        const { hasPiiAccess } = await getDecryptedDataFromLocalStorage('has-pii-access', { hasPiiAccess: false })
        const { hasPiiDownloadAccess } = await getDecryptedDataFromLocalStorage('has-pii-download-access', { hasPiiDownloadAccess: false })
        const { hasPiiEmployeesAccess } = await getDecryptedDataFromLocalStorage('has-pii-employees-access', { hasPiiEmployeesAccess: false })
        const { security } = await getDecryptedDataFromLocalStorage('security', { security: null })
        const { modules } = await getDecryptedDataFromLocalStorage('allowed-modules', { modules: null })

        const account = {
          _id: user.id_account,
          name: user.client,
          id_external: user.id_external,
          is_franchise: user.is_franchise,
          is_franchisee: user.is_franchisee,
          has_mercapromo: user.has_mercapromo,
          only_mercapromo: user.only_mercapromo,
          tribanco: user.tribanco,
          ecommerce_url: user.ecommerce_url,
          recencyCriteria: user.recencyCriteria,
          integrationType: user.integrationType,
          db_name: user.db_name,
          security: security,
          modules: modules,
        }

        this.user = {
          _id: user.id_user,
          name: user.name,
          login: user.username,
          mf: user.mf,
          admin: user.admin,
          multi_user: isMultiUser,
          profiles: user.profiles,
          ecommercePermissions: user.ecommercePermissions,
          club: isClub,
          has_ecommerce: user.has_ecommerce,
          has_club: user.has_club,
          has_pii_access: hasPiiAccess,
          has_pii_employees_access: hasPiiEmployeesAccess,
          has_pii_download_data_permission: hasPiiDownloadAccess,
          account: account,
        }
      } catch (error) {
        console.error('Error getting data from localStorage', error)
        return
      }
    },
    userHasAccessToModule(module) {
      const parsedModules = []
      Object.keys(this.allowedModules ?? {}).forEach((module) => {
        if (this.allowedModules[module]) {
          parsedModules.push(module)
        }
      })
      return parsedModules?.includes(module)
    },
    userHasPagePermission(page) {
      if (this.isMercafacilAdmin || this.pagePermissions?.includes('mf/admin') || !page) {
        return true
      }

      if (this.isFranchisee && this.pagesControlledByFranchiseMap[page]) {
        return this.pagePermissions?.includes(page) && this.franchiseePermissions[this.pagesControlledByFranchiseMap[page]]
      }

      return this.pagePermissions?.includes(page)
    },
  },
  getters: {
    isAdmin: (state) => state.user.admin,
    isMercafacilAdmin: (state) => state.user.mf, // Importante: nome no Vue 2 era *getIsAdmin*
    isMultiUser: (state) => state.user.multi_user,
    isEcommerce: (state) => state.user.has_ecommerce,
    isClub: (state) => state.user.club,
    security: (state) => state.user.account.security,
    allowedModules: (state) => state.user.account.modules,
    hasPiiAccess: (state) => state.user.has_pii_access,
    hasPiiEmployeesAccess: (state) => state.user.has_pii_employees_access,
    hasPiiDownloadAccess: (state) => state.user.has_pii_download_data_permission,
    pagePermissions: (state) => state.user.permissions,
    isAccountOnlyMercapromo: (state) => state.user.account.only_mercapromo,
    hasMercapromo: (state) => state.user.account.has_mercapromo,
    isFranchisee: (state) => state.user.account.is_franchisee,
    franchiseePermissions: (state) => state.user.account.franchiseePermissions,
  },
})
